/**
 * Unlock band for combat abilities (attack + support tabs). Kept aligned with
 * `apps/web/.../player-abilities.ts` catalog — update both when changing levels.
 */
export type PlayerCombatAbilityUnlockEntry =
	| {
			type: "attack";
			level: number;
			name: string;
			description: string;
			imageSrc: string;
	  }
	| {
			type: "support";
			level: number;
			name: string;
			description: string;
			imageSrc: string;
	  };

function isPlayerCombatAbilityLockedAtLevel(
	entry: { level: number },
	playerLevel: number,
): boolean {
	return entry.level > playerLevel;
}

const BATTLE_ATTACK_UNLOCK_ITEMS: readonly PlayerCombatAbilityUnlockEntry[] = [
	{
		type: "attack",
		imageSrc: "/images/battles/attack-moves/staff-bash.png",
		name: "Staff Bash",
		description: "A simple whack with your staff — weak but free.",
		level: 1,
	},
	{
		type: "attack",
		imageSrc: "/images/battles/attack-moves/earth-blast.png",
		name: "Earth Blast",
		description:
			"A charged burst of stone and soil that rakes the front line.",
		level: 5,
	},
	{
		type: "attack",
		imageSrc: "/images/battles/attack-moves/earth-shot.png",
		name: "Earth Shot",
		description: "A quick javelin of packed earth - fast and reliable.",
		level: 2,
	},
	{
		type: "attack",
		imageSrc: "/images/battles/attack-moves/fissure.png",
		name: "Fissure",
		description: "Opens a seam in the ground under your foe.",
		level: 10,
	},
	{
		type: "attack",
		imageSrc: "/images/battles/attack-moves/land-grab.png",
		name: "Land Grab",
		description: "Roots spike upward to snare and strike in one motion.",
		level: 3,
	},
	{
		type: "attack",
		imageSrc: "/images/battles/attack-moves/stone-throw.png",
		name: "Stone Throw",
		description: "Hurls a single heavy stone with brutal aim.",
		level: 2,
	},
];

const BATTLE_SUPPORT_UNLOCK_ITEMS: readonly PlayerCombatAbilityUnlockEntry[] = [
	{
		type: "support",
		imageSrc: "/images/battles/support-moves/staff-block.png",
		name: "Staff Block",
		description:
			"Cross your staffs to parry the foe's next move — a brief defence boost.",
		level: 1,
	},
	{
		type: "support",
		imageSrc: "/images/battles/support-moves/rock-shield.png",
		name: "Rock Shield",
		description:
			"Raise a brief wall of living stone to shield from damage.",
		level: 5,
	},
];

/**
 * Returns attack/support abilities whose required level falls in `(oldLevel, newLevel]`,
 * i.e. newly usable after leveling from `oldLevel` to `newLevel` (matches battle lock rules).
 */
export function getPlayerCombatAbilitiesUnlockedBetweenLevels(
	oldLevel: number,
	newLevel: number,
): PlayerCombatAbilityUnlockEntry[] {
	const prev = Math.floor(oldLevel);
	const next = Math.floor(newLevel);
	if (next <= prev) {
		return [];
	}
	const combined: PlayerCombatAbilityUnlockEntry[] = [
		...BATTLE_ATTACK_UNLOCK_ITEMS,
		...BATTLE_SUPPORT_UNLOCK_ITEMS,
	];
	return combined
		.filter(
			(e) =>
				isPlayerCombatAbilityLockedAtLevel(e, prev) &&
				!isPlayerCombatAbilityLockedAtLevel(e, next),
		)
		.sort((a, b) => {
			const d = a.level - b.level;
			return d !== 0
				? d
				: a.name.localeCompare(b.name, undefined, {
						sensitivity: "base",
					});
		});
}
